import React from 'react';
import { TextField, Autocomplete, FormControl, Button } from '@mui/material';

export default function CompanyStep3Settings({
  formData,
  handleInputChange,
  plans,
  admins,
  newAdminName,
  adminInputValue,
  setAdminInputValue,
  handlePlanChange,
  handleAdminChange,
  handleLogoChange,  
  errors,
  currentUser,
  currentLogoUrl
}) {
  const isDisabled = currentUser?.role === 'ADMIN_CLIENT';

  return (
    <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
      <h4 className="form-section-title" style={{ gridColumn: '1 / -1' }}>Configurações e Limites</h4>
      <Autocomplete
        options={['EAD', 'CLIENT']}
        getOptionLabel={(option) => {
          if (option === 'EAD') return 'EAD (Cria Cursos)';
          if (option === 'CLIENT') return 'Cliente (Consome Cursos)';
          return '';
        }}
        value={currentUser?.role === 'ADMIN_EAD' ? 'CLIENT' : (formData.type || '')}
        onChange={(event, newValue) => {
          const fakeEvent = { target: { name: 'type', value: newValue || '' } };
          handleInputChange(fakeEvent);
        }}
        disabled={currentUser?.role === 'ADMIN_EAD' || isDisabled}
        renderInput={(params) => (
          <TextField {...params} label="Tipo da Empresa" placeholder="Selecione o tipo" required fullWidth />
        )}
        style={{ gridColumn: '1 / -1' }}
      />
      {formData.type !== 'CLIENT' && currentUser?.role !== 'ADMIN_EAD' && (
        <Autocomplete
          options={plans}
          getOptionLabel={(option) => option.name || ''}
          value={plans.find(p => p.id === formData.subscribedPlanId) || null}
          onChange={(event, newValue) => handlePlanChange(newValue)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={isDisabled}
          renderInput={(params) => (
            <TextField {...params} label="Plano Contratado" placeholder="Selecione um Plano" required fullWidth />
          )}
          noOptionsText="Nenhum plano encontrado"
        />
      )}
      {currentUser?.role !== 'ADMIN_EAD' && currentUser?.role !== 'ADMIN_CLIENT' && (
        <Autocomplete
          multiple
          freeSolo
          options={admins}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            if (option.isNew) return option.displayLabel;
            if (newAdminName && option.fullName === newAdminName) return `Criar Usuário - "${newAdminName}"`;
            return `${option.fullName} (${option.email})` || '';
          }}
          value={[
            ...admins.filter(a => formData.adminIds?.includes(a.id)),
            ...(newAdminName ? [{ isNew: true, fullName: newAdminName, displayLabel: `Criar Usuário - "${newAdminName}"` }] : [])
          ]}
          onChange={handleAdminChange}
          onInputChange={(event, newInputValue) => {
            if (event?.type === 'change') {
              setAdminInputValue(newInputValue);
            }
          }}
          filterOptions={(options, params) => {
            const filtered = options.filter(option =>
              option.fullName.toLowerCase().includes(params.inputValue.toLowerCase()) ||
              option.email.toLowerCase().includes(params.inputValue.toLowerCase())
            );
            if (params.inputValue !== '') {
              filtered.unshift({
                isNew: true,
                fullName: params.inputValue,
                displayLabel: `Criar Usuário - "${params.inputValue}"`,
              });
            }
            return filtered;
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={isDisabled}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Admin Responsável"
              placeholder="Digite para buscar ou criar..."
              onKeyDown={(e) => {
                if (e.key === ' ' && adminInputValue.length > 0) e.stopPropagation();
              }}
              fullWidth />
          )}
          noOptionsText="Nenhum admin encontrado"
        />
      )}
      <h4 className="form-section-title" style={{ gridColumn: '1 / -1' }}>Personalização</h4>
      <TextField
        label="Cor Primária"
        type="color"
        name="primaryColor"
        value={formData.primaryColor || '#1976D2'}
        onChange={handleInputChange}
        disabled={isDisabled}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="Cor Secundária"
        type="color"
        name="secondaryColor"
        value={formData.secondaryColor || '#DC004E'}
        onChange={handleInputChange}
        disabled={isDisabled}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      
      {/* Preview da logo atual */}
      {currentLogoUrl && (
        <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: '#1a1a1a', borderRadius: '4px', border: '1px solid #333' }}>
          <span style={{ fontSize: '0.85rem', color: '#b0b0b0', fontWeight: '500' }}>Logo Atual:</span>
          <img 
            src={currentLogoUrl}
            alt="Logo atual"
            style={{ width: '48px', height: '48px', objectFit: 'contain', backgroundColor: '#2a2a2a', borderRadius: '4px', padding: '4px' }}
          />
        </div>
      )}
      
      <FormControl fullWidth style={{ gridColumn: '1 / -1' }}>
        <label htmlFor="logo-upload" style={{ marginBottom: '8px', fontSize: '0.8rem', color: 'var(--secondary-600)' }}>
          Logo da Empresa
        </label>
        <Button
          variant="outlined"
          component="label"
          htmlFor="logo-upload"
          disabled={isDisabled}
        >
          {formData.logo ? `Arquivo: ${formData.logo.name}` : 'Selecionar Arquivo'}
          <input id="logo-upload" type="file" accept="image/*" hidden onChange={handleLogoChange} />
        </Button>
        {formData.logo && <span style={{ fontSize: '0.75rem', marginTop: '4px' }}>Clique para alterar</span>}
      </FormControl>

      <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-4)' }}>
        <TextField
          label="Limite de Instrutores"
          type="number"
          name="trainersLimit"
          value={formData.trainersLimit || 0}
          onChange={handleInputChange}
          disabled={isDisabled}
          inputProps={{ max: 9999, min: 0 }}
          fullWidth
        />
        <TextField
          label="Limite de Alunos"
          type="number"
          name="studentsLimit"
          value={formData.studentsLimit || 0}
          onChange={handleInputChange}
          disabled={isDisabled}
          inputProps={{ max: 9999, min: 0 }}
          fullWidth
        />
        <TextField
          label="Limite de Módulos"
          type="number"
          name="modulesLimit"
          value={formData.modulesLimit || 0}
          onChange={handleInputChange}
          disabled={isDisabled}
          inputProps={{ max: 9999, min: 0 }}
          fullWidth
        />
      </div>
    </div>
  );
}